<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { browser } from '$app/environment';
	import authStore from '$lib/authStore';
	import { MaterialsClient } from '$lib/materialsClient';
	import type { GeneratedMaterial } from '$lib/diffusionApi/models/GeneratedMaterial';
	import Header from '$components/Header.svelte';
	import UsageExceededModal from './UsageExceededModal.svelte';
	import LoginOrSignUpModal from '$components/LoginOrSignUpModal.svelte';
	import PlanInformationClient from '$lib/planInformationClient';
	import type { PlanInformation } from '$lib/diffusionApi';
	import { analytics } from '$lib/analytics';
	import MeterWithValue from '$components/MeterWithValue.svelte';
	import StandaloneAqueductMaterialViewport from '$components/aqueduct/StandaloneAqueductMaterialViewport.svelte';
	import { getMaterialGenerationErrorInfo } from '$lib/materialGenerationErrors';
	import { DEFAULT_MATERIAL_DISPLACEMENT_SCALE } from '$lib/aqueduct/materialRendererDefaults';
	import type { MaterialViewMode } from '$lib/aqueduct/materialViewMode';
	import JSZip from 'jszip';

	let materialViewer: StandaloneAqueductMaterialViewport;

	// View mode and displacement
	let viewMode: Exclude<MaterialViewMode, 'cloth'> = 'cube';
	let displacementScale = DEFAULT_MATERIAL_DISPLACEMENT_SCALE;
	const MIN_DISPLACEMENT = 0.0;
	const MAX_DISPLACEMENT = 0.05;

	// Dark mode state
	let darkMode = browser ? document.documentElement.classList.contains('dark') : false;

	// Constants
	const GENERATION_TIMEOUT_DURATION_SECONDS = 180.0;

	// Material maps bound into the Aqueduct viewport.
	let colorMap: HTMLImageElement | null = null;
	let heightMap: HTMLImageElement | null = null;
	let normalMap: HTMLImageElement | null = null;
	let metallicMap: HTMLImageElement | null = null;
	let roughnessMap: HTMLImageElement | null = null;
	let aoMap: HTMLImageElement | null = null;

	// Generation state
	let generatedMaterial: GeneratedMaterial | null = null;
	let isGenerating = false;
	let generationTimer = 0;
	let generationInterval: ReturnType<typeof setInterval> | null = null;
	let materialsClient: MaterialsClient = new MaterialsClient('');
	let errorMessage = '';

	// Plan information
	let planInformationClient: PlanInformationClient = new PlanInformationClient('');
	let planInformation: PlanInformation | undefined = undefined;
	let usageExceededModal: UsageExceededModal;
	let usageExceededErrorDetail: string = '';
	let loginOrSignUpModal: LoginOrSignUpModal;

	// Material history
	let materialHistory: GeneratedMaterial[] = [];
	let loadingHistory = false;
	let historyInitialized = false;

	// Custom instructions
	let customInstructions = '';

	// Update client token when auth changes
	let dataInitialized = false;
	$: if ($authStore.user && browser && !dataInitialized) {
		dataInitialized = true;
		$authStore.user.getIdToken().then(async (token) => {
			materialsClient.setToken(token);
			planInformationClient.setToken(token);
			try {
				planInformation = await planInformationClient.getPlanInformation();
				if (planInformation) {
					analytics.setPersonProperties({ planName: planInformation.name });
				}
			} catch (error) {
				console.error('Failed to fetch plan information:', error);
			}
			fetchMaterialHistory();
		});
	}

	let observer: MutationObserver | null = null;

	onMount(() => {
		if (!browser) {
			return;
		}

		syncThemeSurface();

		// Watch for theme changes
		observer = new MutationObserver((mutations) => {
			syncThemeSurface();
		});
		observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

		// Load albedo from sessionStorage if available and recent
		loadAlbedoFromSessionStorage();

		return () => {
			if (observer) {
				observer.disconnect();
			}
			if (generationInterval) {
				clearInterval(generationInterval);
			}
		};
	});

	onDestroy(() => {
		if (observer) {
			observer.disconnect();
		}
		if (generationInterval) {
			clearInterval(generationInterval);
		}
	});

	function syncThemeSurface(): void {
		darkMode = document.documentElement.classList.contains('dark');
		document.body.style.backgroundColor = darkMode ? '#101016' : '#FFFEEE';
	}

	function handleFileSelect(file: File) {
		if (file && file.type.startsWith('image/')) {
			const reader = new FileReader();
			reader.onload = (event) => {
				const img = new Image();
				img.onload = () => {
					materialViewer?.updateMaterialMap('map', img);

					analytics.capture('albedo_map_uploaded', {
						width: img.width,
						height: img.height
					});

					// Reset all PBR maps when new albedo is loaded
					generatedMaterial = null;
					materialViewer?.clearPBRMaps();
				};
				img.src = event.target?.result as string;
			};
			reader.readAsDataURL(file);
		}
	}

	function openFileUploader() {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		input.onchange = (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) handleFileSelect(file);
		};
		input.click();
	}

	async function generatePBRMaps() {
		if (!colorMap || !materialsClient) {
			console.error('Missing requirements for generation');
			return;
		}

		if (!$authStore.user) {
			loginOrSignUpModal.openDialog();
			return;
		}

		try {
			const idToken = await $authStore.user.getIdToken();
			materialsClient.setToken(idToken);
		} catch (error) {
			console.error('Failed to get authentication token:', error);
			errorMessage = 'Authentication failed. Please try signing in again.';
			return;
		}

		isGenerating = true;
		generationTimer = 0;
		errorMessage = '';

		analytics.capture('generate_pbr_maps_pressed');

		generationInterval = setInterval(() => {
			generationTimer += 0.1;
			if (generationTimer >= GENERATION_TIMEOUT_DURATION_SECONDS) {
				console.error(`Generation timed out after ${GENERATION_TIMEOUT_DURATION_SECONDS} seconds`);
				errorMessage = `Generation timed out after ${GENERATION_TIMEOUT_DURATION_SECONDS} seconds`;

				isGenerating = false;
				if (generationInterval) {
					clearInterval(generationInterval);
					generationInterval = null;
				}
				generationTimer = 0;
			}
		}, 100);

		try {
			const canvas = document.createElement('canvas');
			canvas.width = colorMap.width;
			canvas.height = colorMap.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) throw new Error('Could not get canvas context');

			ctx.drawImage(colorMap, 0, 0);
			const dataUrl = canvas.toDataURL('image/png');
			const base64Data = dataUrl.split(',')[1];

			console.log('Calling createMaterial with image size:', colorMap.width, 'x', colorMap.height);

			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(
					() => reject(new Error('Request timeout')),
					GENERATION_TIMEOUT_DURATION_SECONDS * 1000
				);
			});

			const response = (await Promise.race([
				materialsClient.createMaterial(base64Data, customInstructions || undefined),
				timeoutPromise
			])) as any;

			analytics.capture('material_request_submitted', {
				image_width: colorMap.width,
				image_height: colorMap.height,
				has_custom_instructions: !!customInstructions
			});

			if (response && response.material) {
				generatedMaterial = response.material;
				console.log('Material generated successfully:', response.material);

				if (response.material.height) {
					loadTextureFromUrl(response.material.height, 'height');
				}
				if (response.material.normal) {
					loadTextureFromUrl(response.material.normal, 'normal');
				}
				if (response.material.roughness) {
					loadTextureFromUrl(response.material.roughness, 'roughness');
				}
				if (response.material.metallic) {
					loadTextureFromUrl(response.material.metallic, 'metallic');
				}
				if (response.material.ambient_occlusion) {
					loadTextureFromUrl(response.material.ambient_occlusion, 'ao');
				}

				analytics.capture('pbr_maps_generated', {
					material_id: response.material.generation_id,
					has_height: !!response.material.height,
					has_normal: !!response.material.normal,
					has_roughness: !!response.material.roughness,
					has_metallic: !!response.material.metallic,
					has_ao: !!response.material.ambient_occlusion,
					generation_time: generationTimer.toFixed(1)
				});

				fetchMaterialHistory();
			} else {
				console.error('No material in response:', response);
				errorMessage = 'Failed to generate PBR maps';
			}
		} catch (error: unknown) {
			const errorInfo = getMaterialGenerationErrorInfo(error);

			if (errorInfo.code === 'MATERIAL_GENERATION_RECITATION' && errorInfo.message) {
				errorMessage = `Failed to generate PBR maps: ${errorInfo.message}`;
			} else if (errorInfo.status === 403 || errorInfo.message?.includes('usage limit')) {
				const errorDetail = errorInfo.message ?? '';
				console.log('403 error detail:', errorDetail);
				if (errorDetail.includes('usage limit')) {
					usageExceededErrorDetail = errorDetail;
					usageExceededModal.openDialog();
					errorMessage = '';
				} else {
					errorMessage = 'Access denied';
				}
			} else if (errorInfo.status === 400) {
				const errorDetail = errorInfo.message ?? '';
				if (errorDetail.includes('INVALID_IMAGE_DIMENSIONS')) {
					errorMessage = 'Uploads must be square base color maps between 256x256 and 4096x4096';

					analytics.capture('material_request_rejected_invalid_dimensions', {
						image_width: colorMap?.width,
						image_height: colorMap?.height,
						error_detail: errorDetail
					});
				} else {
					errorMessage = errorDetail || 'Invalid request';
				}
			} else if (error instanceof Error && error.message === 'Request timeout') {
				errorMessage = `Generation timed out after ${GENERATION_TIMEOUT_DURATION_SECONDS} seconds`;
			} else if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
				errorMessage = 'Could not connect to the server';
			} else {
				errorMessage = 'Failed to generate PBR maps';
			}
		} finally {
			isGenerating = false;
			if (generationInterval) {
				clearInterval(generationInterval);
				generationInterval = null;
			}
			if (!generatedMaterial) {
				generationTimer = 0;
			}
		}
	}

	function loadTextureFromUrl(
		url: string,
		mapType: 'height' | 'normal' | 'metallic' | 'roughness' | 'ao'
	) {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			const materialMapType = getMaterialMapType(mapType);
			materialViewer?.updateMaterialMap(materialMapType, img);
		};
		img.src = url;
	}

	function getMaterialMapType(
		mapType: 'height' | 'normal' | 'metallic' | 'roughness' | 'ao'
	): 'displacementMap' | 'normalMap' | 'metalnessMap' | 'roughnessMap' | 'aoMap' {
		switch (mapType) {
			case 'height':
				return 'displacementMap';
			case 'normal':
				return 'normalMap';
			case 'metallic':
				return 'metalnessMap';
			case 'roughness':
				return 'roughnessMap';
			case 'ao':
				return 'aoMap';
		}
	}

	async function downloadGeneratedMap(
		mapType: 'height' | 'normal' | 'roughness' | 'metallic' | 'ambient_occlusion'
	) {
		if (!generatedMaterial) return;

		const mapUrls = {
			height: generatedMaterial.height,
			normal: generatedMaterial.normal,
			roughness: generatedMaterial.roughness,
			metallic: generatedMaterial.metallic,
			ambient_occlusion: generatedMaterial.ambient_occlusion
		};

		const url = mapUrls[mapType];

		if (!url) return;

		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);

			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = `${mapType}.png`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);

			URL.revokeObjectURL(blobUrl);

			analytics.capture(`${mapType}_map_downloaded`, {
				map_type: mapType,
				material_id: generatedMaterial.generation_id,
				map_url: url
			});

			if (generatedMaterial.generation_id) {
				materialsClient.markAsDownloaded(generatedMaterial.generation_id);
			}
		} catch (error) {
			console.error('Failed to download map:', error);
			window.open(url, '_blank');
		}
	}

	function downloadAlbedo() {
		if (!colorMap) return;

		const canvas = document.createElement('canvas');
		canvas.width = colorMap.width;
		canvas.height = colorMap.height;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.drawImage(colorMap, 0, 0);
		canvas.toBlob((blob) => {
			if (!blob) return;
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = 'base_color.png';
			a.click();
			URL.revokeObjectURL(url);

			analytics.capture('albedo_map_downloaded', {
				material_id: generatedMaterial?.generation_id,
				map_url: generatedMaterial?.albedo
			});

			if (generatedMaterial?.generation_id) {
				materialsClient.markAsDownloaded(generatedMaterial.generation_id);
			}
		});
	}

	let isDownloadingZip = false;

	async function downloadAllAsZip() {
		if (!colorMap || isDownloadingZip) return;

		isDownloadingZip = true;

		try {
			const zip = new JSZip();

			// Add albedo from canvas
			const canvas = document.createElement('canvas');
			canvas.width = colorMap.width;
			canvas.height = colorMap.height;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.drawImage(colorMap, 0, 0);
				const blob = await new Promise<Blob | null>((resolve) =>
					canvas.toBlob(resolve, 'image/png')
				);
				if (blob) {
					zip.file('base_color.png', blob);
				}
			}

			// Add generated maps from R2 URLs
			const mapEntries: { name: string; url: string | null | undefined }[] = [
				{ name: 'height.png', url: generatedMaterial?.height },
				{ name: 'normal.png', url: generatedMaterial?.normal },
				{ name: 'roughness.png', url: generatedMaterial?.roughness },
				{ name: 'metallic.png', url: generatedMaterial?.metallic },
				{ name: 'ao.png', url: generatedMaterial?.ambient_occlusion }
			];

			await Promise.all(
				mapEntries
					.filter((entry): entry is { name: string; url: string } => !!entry.url)
					.map(async (entry) => {
						const response = await fetch(entry.url);
						const blob = await response.blob();
						zip.file(entry.name, blob);
					})
			);

			const content = await zip.generateAsync({ type: 'blob' });
			const url = URL.createObjectURL(content);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${generatedMaterial?.title || 'material'}.zip`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);

			analytics.capture('all_maps_downloaded_zip', {
				material_id: generatedMaterial?.generation_id,
				material_title: generatedMaterial?.title
			});

			if (generatedMaterial?.generation_id) {
				materialsClient.markAsDownloaded(generatedMaterial.generation_id);
			}
		} catch (error) {
			console.error('Failed to download zip:', error);
		} finally {
			isDownloadingZip = false;
		}
	}

	function formatTimer(seconds: number) {
		return seconds.toFixed(1);
	}

	async function fetchMaterialHistory() {
		if (!$authStore.user || loadingHistory) return;

		if (!historyInitialized) {
			loadingHistory = true;
		}
		historyInitialized = true;

		try {
			const idToken = await $authStore.user.getIdToken();
			materialsClient.setToken(idToken);

			const materials = await materialsClient.getMaterials($authStore.user.uid, {
				most_recent_first: true,
				limit: 100
			});
			console.log('Fetched materials:', materials.length);
			materialHistory = materials;
		} catch (error) {
			console.error('Failed to fetch material history:', error);
		} finally {
			loadingHistory = false;
		}
	}

	function restoreMaterial(material: GeneratedMaterial) {
		if (!material) return;

		analytics.capture('history_material_restored', {
			material_id: material.generation_id,
			material_title: material.title || 'Untitled'
		});

		generatedMaterial = material;

		if (material.height) {
			loadTextureFromUrl(material.height, 'height');
		}
		if (material.normal) {
			loadTextureFromUrl(material.normal, 'normal');
		}
		if (material.roughness) {
			loadTextureFromUrl(material.roughness, 'roughness');
		}
		if (material.metallic) {
			loadTextureFromUrl(material.metallic, 'metallic');
		}
		if (material.ambient_occlusion) {
			loadTextureFromUrl(material.ambient_occlusion, 'ao');
		}

		if (material.albedo) {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => {
				materialViewer?.updateMaterialMap('map', img);
			};
			img.src = material.albedo;
		}
	}

	function truncateTitle(title: string | null | undefined): string {
		if (!title) return 'Untitled Material';
		if (title.length <= 24) return title;
		return title.substring(0, 24) + '...';
	}

	function switchToCube() {
		if (viewMode === 'cube') return;
		viewMode = 'cube';
	}

	function switchToSphere() {
		if (viewMode === 'sphere') return;
		viewMode = 'sphere';
	}

	function switchToWall() {
		if (viewMode === 'plane') return;
		viewMode = 'plane';
	}

	function loadAlbedoFromSessionStorage() {
		if (!browser) return;

		try {
			const storedData = localStorage.getItem('materialGeneratorAlbedo');
			if (!storedData) return;

			const materialData = JSON.parse(storedData);
			const { dataUrl, storedAt } = materialData;

			const age = Date.now() - storedAt;
			if (age > 10000) {
				localStorage.removeItem('materialGeneratorAlbedo');
				return;
			}

			const img = new Image();
			img.onload = () => {
				materialViewer?.updateMaterialMap('map', img);

				analytics.capture('albedo_loaded_from_canvas', {
					width: img.width,
					height: img.height
				});

				localStorage.removeItem('materialGeneratorAlbedo');
			};
			img.onerror = (error) => {
				console.error('Failed to load image from localStorage:', error);
			};
			img.src = dataUrl;
		} catch (error) {
			console.error('Failed to load albedo from localStorage:', error);
			localStorage.removeItem('materialGeneratorAlbedo');
		}
	}

	function handleCanvasDrop(e: DragEvent) {
		if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
			const file = e.dataTransfer.files[0];
			handleFileSelect(file);
		}
	}
</script>

<svelte:head>
	<title>Material Generator - Iliad</title>
	<meta name="description" content="Create PBR material maps with Iliad's Material Generator" />
</svelte:head>

<div
	role="presentation"
	on:dragover|preventDefault
	on:drop|preventDefault={handleCanvasDrop}
	class="absolute inset-0 w-screen h-screen"
>
	<StandaloneAqueductMaterialViewport
		bind:this={materialViewer}
		bind:colorMap
		bind:heightMap
		bind:normalMap
		bind:metallicMap
		bind:roughnessMap
		bind:aoMap
		docId="material-generator"
		{viewMode}
		isDark={darkMode}
		{displacementScale}
		enableMomentum
		warningDescription="Material Generator requires WebGPU to preview generated PBR materials on your GPU."
	/>
</div>

<!-- Header -->
<div class="fixed top-2 left-2 z-20 flex flex-row items-center">
	<Header fixed={true} />
	<div class="w-2"></div>
	<h1 class="px-3 text-xs sm:text-sm font-semibold text-zinc-900 dark:text-zinc-200">
		Material Generator
	</h1>
</div>

<!-- History (left side) -->
<div
	class="fixed left-2 top-14 z-10 flex flex-col bg-primary-75 dark:bg-zinc-900 border border-primary-200 dark:border-zinc-800 rounded p-1 shadow-md overflow-hidden w-[160px]"
>
	<h3 class="text-xs font-semibold text-zinc-700 dark:text-zinc-300 p-1">History</h3>

	{#if loadingHistory}
		<div class="text-xs text-zinc-500 dark:text-zinc-400 p-1">Loading...</div>
	{:else if materialHistory.length === 0}
		<div class="text-xs text-zinc-500 dark:text-zinc-400 p-1">No materials yet.</div>
	{:else}
		<div class="flex flex-col overflow-y-auto scroller max-h-[calc(100vh-17rem)]">
			{#each materialHistory as material}
				<button
					on:click={() => restoreMaterial(material)}
					class="p-1 rounded text-xs text-left transition-colors
						{generatedMaterial?.generation_id === material.generation_id
						? 'bg-primary-100 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100'
						: 'bg-primary-75 hover:brightness-90 text-zinc-900 dark:bg-zinc-900 dark:hover:brightness-150 dark:text-zinc-300'}"
					title={material.title || 'Untitled Material'}
				>
					{truncateTitle(material.title)}
				</button>
			{/each}
		</div>
	{/if}
</div>

<!-- Controls -->
{#if !colorMap}
	<!-- Initial upload state -->
	<div class="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center z-10">
		<button
			on:click={openFileUploader}
			class="px-8 py-4 rounded-lg font-medium shadow-md text-lg border
				bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
				dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
		>
			Upload base color map
		</button>
		<div class="mt-4 px-6 py-3">
			<p class="text-sm text-zinc-600 dark:text-zinc-400">
				Drop or upload a base color map to get started. Or,
				<a
					href="/canvas?tilingMode=true"
					class="text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 cursor-pointer underline"
				>
					head to the Canvas to generate one
				</a>.
			</p>
		</div>
	</div>
{:else}
	<!-- Bottom controls container -->
	<div class="fixed bottom-2 left-2 flex flex-col gap-2 z-10">
		<!-- Custom instructions textarea - fixed dimensions -->
		<div
			class="w-[calc(22rem+0.75rem+1rem)] p-2 bg-primary-75 border border-primary-200 rounded shadow-md dark:bg-zinc-900 dark:border-zinc-800"
		>
			<label
				for="custom-instructions"
				class="text-xs font-semibold text-zinc-700 dark:text-zinc-300 block mb-1"
			>
				Custom instructions (optional)
			</label>
			<textarea
				id="custom-instructions"
				bind:value={customInstructions}
				maxlength="2048"
				placeholder="Provide optional, custom instructions for PBR map generation..."
				class="w-full h-[60px] px-2 py-1 text-xs rounded border resize-none
					bg-primary-50 border-primary-200 text-zinc-900 placeholder-zinc-500
					dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-200 dark:placeholder-zinc-500
					focus:outline-none focus:ring-2 focus:ring-primary-400 dark:focus:ring-zinc-600"
			></textarea>
		</div>

		<!-- Button toolbar with fixed width container -->
		<div class="w-[calc(22rem+0.75rem+1rem)] flex flex-row items-center gap-3">
			<div
				class="flex-shrink-0 p-2 flex flex-row justify-start items-center
				bg-primary-75 border border-primary-200 rounded shadow-md gap-3
				dark:bg-zinc-900 dark:border-zinc-800"
			>
				<button
					on:click={openFileUploader}
					class="w-44 py-2 rounded shadow-md text-sm font-medium border
						bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
						dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
				>
					Upload new base color
				</button>
				<button
					on:click={generatePBRMaps}
					disabled={isGenerating}
					class="w-44 py-2 rounded shadow-md text-sm font-medium border disabled:opacity-50 disabled:cursor-not-allowed
						bg-zinc-900 border-zinc-800 hover:brightness-90 text-zinc-100
						dark:bg-zinc-100 dark:border-zinc-200 dark:hover:brightness-125 dark:text-zinc-900"
				>
					{generatedMaterial ? 'Re-generate' : 'Generate'} PBR maps
				</button>
			</div>

			<!-- Processing indicator or error message (separate, outside button container) -->
			{#if isGenerating}
				<div
					class="flex-shrink-0 text-sm font-medium text-zinc-900 dark:text-zinc-300 whitespace-nowrap"
				>
					Processing {formatTimer(generationTimer)} / {GENERATION_TIMEOUT_DURATION_SECONDS.toFixed(
						1
					)}
				</div>
			{:else if errorMessage}
				<div
					class="flex-shrink-0 text-sm font-medium text-warning-700 dark:text-warning-300 whitespace-nowrap"
				>
					{errorMessage}
				</div>
			{/if}
		</div>
	</div>
{/if}

<!-- Right side panels container (always visible) -->
<div class="fixed right-2 top-2 flex flex-col gap-2 z-10 max-h-[calc(100vh-1rem)]">
	<!-- View mode toggle panel -->
	<div
		class="w-[180px] bg-primary-75 dark:bg-zinc-900 border border-primary-200 dark:border-zinc-800 rounded p-3 shadow-md"
	>
		<h3 class="text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-2">View</h3>
		<div class="flex flex-col gap-1">
			<button
				on:click={switchToCube}
				aria-label="Switch to cube view"
				class="px-3 py-1.5 rounded text-xs text-left border transition-colors flex items-center
					{viewMode === 'cube'
					? 'bg-primary-100 border-primary-300 text-zinc-900 dark:bg-zinc-700 dark:border-zinc-600 dark:text-zinc-100'
					: 'bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-150 dark:text-zinc-300'}"
			>
				<i class="fa-solid fa-cube text-[10px] mr-2"></i>
				Cube
			</button>
			<button
				on:click={switchToSphere}
				aria-label="Switch to sphere view"
				class="px-3 py-1.5 rounded text-xs text-left border transition-colors flex items-center
					{viewMode === 'sphere'
					? 'bg-primary-100 border-primary-300 text-zinc-900 dark:bg-zinc-700 dark:border-zinc-600 dark:text-zinc-100'
					: 'bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-150 dark:text-zinc-300'}"
			>
				<i class="fa-solid fa-circle text-[10px] mr-2"></i>
				Sphere
			</button>
			<button
				on:click={switchToWall}
				aria-label="Switch to plane view"
				class="px-3 py-1.5 rounded text-xs text-left border transition-colors flex items-center
					{viewMode === 'plane'
					? 'bg-primary-100 border-primary-300 text-zinc-900 dark:bg-zinc-700 dark:border-zinc-600 dark:text-zinc-100'
					: 'bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-150 dark:text-zinc-300'}"
			>
				<i class="fa-solid fa-square text-[10px] mr-2"></i>
				Plane
			</button>
		</div>

		<!-- Displacement Scale Slider -->
		<div class="mt-3">
			<MeterWithValue
				bind:value={displacementScale}
				setValue={(v) => (displacementScale = v)}
				min={MIN_DISPLACEMENT}
				max={MAX_DISPLACEMENT}
				step={0.005}
				name="Displacement Scale"
				isFloat={true}
			/>
		</div>
	</div>

	<!-- Download buttons -->
	{#if generatedMaterial || colorMap}
		<div
			class="w-[180px] flex flex-col gap-1 bg-primary-75 dark:bg-zinc-900 border border-primary-200 dark:border-zinc-800 rounded p-3 shadow-md"
		>
			<h3 class="text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1">Download Maps</h3>

			{#if colorMap}
				<div class="flex items-center gap-2">
					<img
						src={colorMap.src}
						alt="Base color map"
						class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 object-cover"
					/>
					<button
						on:click={downloadAlbedo}
						class="flex-1 px-3 py-1.5 rounded text-xs text-left border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						<i class="fa-solid fa-download mr-2 text-xs"></i>
						Base Color
					</button>
				</div>
			{/if}

			{#if generatedMaterial?.height}
				<div class="flex items-center gap-2">
					{#if heightMap}
						<img
							src={heightMap.src}
							alt="Height map"
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 object-cover"
						/>
					{:else}
						<div
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 bg-zinc-200 dark:bg-zinc-700"
						></div>
					{/if}
					<button
						on:click={() => downloadGeneratedMap('height')}
						class="flex-1 px-3 py-1.5 rounded text-xs text-left border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						<i class="fa-solid fa-download mr-2 text-xs"></i>
						Height
					</button>
				</div>
			{/if}

			{#if generatedMaterial?.normal}
				<div class="flex items-center gap-2">
					{#if normalMap}
						<img
							src={normalMap.src}
							alt="Normal map"
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 object-cover"
						/>
					{:else}
						<div
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 bg-zinc-200 dark:bg-zinc-700"
						></div>
					{/if}
					<button
						on:click={() => downloadGeneratedMap('normal')}
						class="flex-1 px-3 py-1.5 rounded text-xs text-left border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						<i class="fa-solid fa-download mr-2 text-xs"></i>
						Normal
					</button>
				</div>
			{/if}

			{#if generatedMaterial?.roughness}
				<div class="flex items-center gap-2">
					{#if roughnessMap}
						<img
							src={roughnessMap.src}
							alt="Roughness map"
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 object-cover"
						/>
					{:else}
						<div
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 bg-zinc-200 dark:bg-zinc-700"
						></div>
					{/if}
					<button
						on:click={() => downloadGeneratedMap('roughness')}
						class="flex-1 px-3 py-1.5 rounded text-xs text-left border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						<i class="fa-solid fa-download mr-2 text-xs"></i>
						Roughness
					</button>
				</div>
			{/if}

			{#if generatedMaterial?.metallic}
				<div class="flex items-center gap-2">
					{#if metallicMap}
						<img
							src={metallicMap.src}
							alt="Metallic map"
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 object-cover"
						/>
					{:else}
						<div
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 bg-zinc-200 dark:bg-zinc-700"
						></div>
					{/if}
					<button
						on:click={() => downloadGeneratedMap('metallic')}
						class="flex-1 px-3 py-1.5 rounded text-xs text-left border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						<i class="fa-solid fa-download mr-2 text-xs"></i>
						Metallic
					</button>
				</div>
			{/if}

			{#if generatedMaterial?.ambient_occlusion}
				<div class="flex items-center gap-2">
					{#if aoMap}
						<img
							src={aoMap.src}
							alt="Ambient occlusion map"
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 object-cover"
						/>
					{:else}
						<div
							class="w-7 h-7 rounded border border-primary-200 dark:border-zinc-700 bg-zinc-200 dark:bg-zinc-700"
						></div>
					{/if}
					<button
						on:click={() => downloadGeneratedMap('ambient_occlusion')}
						class="flex-1 px-3 py-1.5 rounded text-xs text-left border
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						<i class="fa-solid fa-download mr-2 text-xs"></i>
						AO
					</button>
				</div>
			{/if}

			{#if generatedMaterial}
				<div class="mt-1 pt-2">
					<button
						on:click={downloadAllAsZip}
						disabled={isDownloadingZip}
						class="w-full px-3 py-1.5 rounded text-xs font-medium border disabled:opacity-50 disabled:cursor-not-allowed
							bg-primary-50 border-primary-200 hover:brightness-90 text-zinc-900
							dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150 dark:text-zinc-300"
					>
						{#if isDownloadingZip}
							<i class="fa-solid fa-circle-notch fa-spin mr-2 text-xs"></i>
						{:else}
							<i class="fa-solid fa-download mr-2 text-xs"></i>
						{/if}
						All PBR maps .zip
					</button>
				</div>
			{/if}
		</div>
	{/if}
</div>

<UsageExceededModal bind:this={usageExceededModal} {usageExceededErrorDetail} />
<LoginOrSignUpModal
	bind:this={loginOrSignUpModal}
	currentUser={$authStore.user ?? undefined}
	whichModal="sign-up"
/>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
		overflow: hidden;
	}
</style>
